/*
 * FICHIER: FullScreenImageViewer.js
 * BUT: Visualiseur d'images en plein écran avec navigation
 * 
 * Ce composant fournit une interface de visualisation d'images en plein écran :
 * - Affichage d'une ou plusieurs images en overlay modal
 * - Navigation entre images avec boutons et clavier
 * - Fermeture par clic extérieur, bouton ou touche Escape
 * - Interface responsive et accessible
 * - Compteur d'images pour les galeries
 * - Gestion du scroll de la page (blocage pendant l'affichage)
 * 
 * FONCTIONNALITÉS AVANCÉES :
 * - Support image unique ou galerie multiple
 * - Navigation clavier (flèches gauche/droite, Escape)
 * - Navigation tactile et clavier responsive
 * - Positionnement adaptatif des boutons sur mobile
 * - Prévention des clics propagés
 * - Gestion automatique de l'overflow du body
 * 
 * PATTERN :
 * - Modal overlay avec portal-like behavior
 * - Event listeners globaux pour le clavier
 * - State management pour l'index courant
 * - Conditional rendering selon le nombre d'images
 */

import React, { useEffect, useState } from 'react';
import styled from 'styled-components';         // CSS-in-JS pour styles encapsulés
import { 
  FaTimes,          // Icône fermeture
  FaChevronLeft,    // Icône flèche gauche
  FaChevronRight    // Icône flèche droite
} from 'react-icons/fa';

// STYLED COMPONENTS - INTERFACE PLEIN ÉCRAN

/**
 * OVERLAY: Arrière-plan modal couvrant tout l'écran
 */
const ImageOverlay = styled.div`
  position: fixed;                    // Position fixe par rapport au viewport
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.9);  // Arrière-plan noir semi-transparent
  display: flex;
  justify-content: center;            // Centrage horizontal
  align-items: center;                // Centrage vertical
  z-index: 2000;                      // Z-index élevé pour être au-dessus de tout
  padding: 20px;                      // Espacement pour éviter les bords
`;

/**
 * WRAPPER IMAGE: Container pour l'image et les contrôles
 */
const ImageWrapper = styled.div`
  position: relative;                 // Positionnement relatif pour les boutons absolus
  max-width: 90vw;                    // Largeur max = 90% du viewport
  max-height: 90vh;                   // Hauteur max = 90% du viewport
  display: flex;
  justify-content: center;
  align-items: center;
`;

/**
 * IMAGE: L'image affichée en plein écran
 */
const Image = styled.img`
  max-width: 100%;                    // Ne déborde pas du container
  max-height: 90vh;                   // Hauteur maximale pour éviter les débordements
  object-fit: contain;                // Garde les proportions sans déformation
  box-shadow: 0 5px 25px rgba(0, 0, 0, 0.3);  // Ombre portée pour la profondeur
`;

/**
 * BOUTON FERMER: X positionné en haut à droite
 */
const CloseButton = styled.button`
  position: absolute;
  top: -40px;                         // Au-dessus de l'image
  right: -40px;                       // À droite de l'image
  background: none;                   // Pas de fond
  border: none;                       // Pas de bordure
  font-size: 2rem;                    // Grande taille pour être bien visible
  color: white;                       // Couleur blanche pour contraste
  cursor: pointer;
  padding: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.3s ease;       // Animation couleur

  &:hover {
    color: #4a6bce;                   // Bleu au survol
  }
  
  // RESPONSIVE: Repositionnement sur mobile
  @media screen and (max-width: 768px) {
    top: -40px;
    right: 0;                         // Aligné à droite du container sur mobile
  }
`;

/**
 * BOUTONS NAVIGATION: Flèches gauche/droite pour les galeries
 */
const NavButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translate3d(0, -50%, 0);
  transform-origin: center center;
  will-change: transform;
  line-height: 0;
  outline: none;

  // POSITIONNEMENT DYNAMIQUE selon la direction
  ${props => props.direction === 'left' ? 'left: -60px;' : 'right: -60px;'}
  
  background: none;
  border: none;
  font-size: 2rem;
  color: white;
  cursor: pointer;
  padding: 15px;                      // Zone de clic généreuse
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.3s ease, transform 0.15s ease-out;
  z-index: 2100;                      // Au-dessus de l'image

  &:hover {
    color: #4a6bce;                   // Bleu au survol
    transform: translate3d(0, -50%, 0); // Conserver la même position verticale au hover
  }
  
  // RESPONSIVE: Repositionnement sur mobile
  @media screen and (max-width: 768px) {
    ${props => props.direction === 'left' ? 'left: 10px;' : 'right: 10px;'}
    background: none;                 // Garde la transparence sur mobile
    border-radius: 0;
    padding: 15px;
  }
`;

/**
 * COMPTEUR: Affichage "X / Y" pour les galeries
 */
const ImageCounter = styled.div`
  position: absolute;
  bottom: -30px;                      // En dessous de l'image
  left: 0;
  right: 0;
  text-align: center;                 // Centré horizontalement
  color: white;
  font-size: 1rem;
`;

/**
 * COMPOSANT PRINCIPAL: Visualiseur d'images plein écran
 * 
 * Gère l'affichage d'une ou plusieurs images en plein écran avec
 * navigation complète et contrôles clavier/souris.
 * 
 * @param {string|Array} images - URL d'image ou tableau d'URLs
 * @param {number} currentIndex - Index de l'image à afficher initialement
 * @param {Function} onClose - Callback de fermeture
 * @param {Function} onPrev - Callback navigation précédente (optionnel)
 * @param {Function} onNext - Callback navigation suivante (optionnel)
 * @returns {JSX.Element} Interface de visualisation plein écran
 */
const FullScreenImageViewer = ({ images, currentIndex = 0, onClose, onPrev, onNext }) => {
  // ÉTAT LOCAL: Index de l'image courante
  const [index, setIndex] = useState(currentIndex);
  
  // DÉTECTION: Galerie multiple ou image unique
  const hasMultipleImages = Array.isArray(images) && images.length > 1;
  
  // NORMALISATION: Convertir en tableau pour traitement uniforme
  const imageSources = Array.isArray(images) ? images : [images];
  const currentImage = imageSources[index];
  
  /**
   * NAVIGATION PRÉCÉDENTE
   * 
   * Passe à l'image précédente avec bouclage circulaire.
   * Utilise le callback externe si fourni, sinon gère localement.
   */
  const handlePrevImage = (e) => {
    e.stopPropagation();              // Empêche la fermeture par clic overlay
    
    if (onPrev) {
      onPrev(e);                      // Utilise la logique externe
    } else {
      // LOGIQUE LOCALE: Bouclage circulaire
      setIndex(prev => (prev === 0 ? imageSources.length - 1 : prev - 1));
    }
  };
  
  /**
   * NAVIGATION SUIVANTE
   * 
   * Passe à l'image suivante avec bouclage circulaire.
   * Utilise le callback externe si fourni, sinon gère localement.
   */
  const handleNextImage = (e) => {
    e.stopPropagation();              // Empêche la fermeture par clic overlay
    
    if (onNext) {
      onNext(e);                      // Utilise la logique externe
    } else {
      // LOGIQUE LOCALE: Bouclage circulaire
      setIndex(prev => (prev === imageSources.length - 1 ? 0 : prev + 1));
    }
  };
  
  // EFFET: Gestion des événements clavier et scroll
  useEffect(() => {
    /**
     * GESTIONNAIRE CLAVIER
     * 
     * Gère les touches Escape (fermeture) et flèches (navigation).
     */
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();                    // Fermeture par Escape
      } else if (hasMultipleImages) {
        if (e.key === 'ArrowLeft') {
          handlePrevImage(e);         // Navigation gauche
        } else if (e.key === 'ArrowRight') {
          handleNextImage(e);         // Navigation droite
        }
      }
    };
    
    // AJOUT du listener clavier global
    window.addEventListener('keydown', handleKeyDown);
    
    // BLOCAGE du scroll de la page pendant l'affichage
    document.body.style.overflow = 'hidden';
    
    // CLEANUP: Restauration à la fermeture
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'auto';    // Restaure le scroll
    };
  }, [onClose, hasMultipleImages, handlePrevImage, handleNextImage]);

  return (
    <ImageOverlay onClick={onClose}>
      <ImageWrapper onClick={(e) => e.stopPropagation()}>
        
        {/* IMAGE PRINCIPALE */}
        <Image src={currentImage} alt={`Image ${index + 1}`} />
        
        {/* BOUTON FERMER */}
        <CloseButton onClick={onClose}>
          <FaTimes />
        </CloseButton>
        
        {/* CONTRÔLES NAVIGATION (seulement pour les galeries multiples) */}
        {hasMultipleImages && (
          <>
            {/* BOUTON PRÉCÉDENT */}
            <NavButton direction="left" onClick={handlePrevImage}>
              <FaChevronLeft />
            </NavButton>
            
            {/* BOUTON SUIVANT */}
            <NavButton direction="right" onClick={handleNextImage}>
              <FaChevronRight />
            </NavButton>
            
            {/* COMPTEUR D'IMAGES */}
            <ImageCounter>
              {index + 1} / {imageSources.length}
            </ImageCounter>
          </>
        )}
      </ImageWrapper>
    </ImageOverlay>
  );
};

export default FullScreenImageViewer; 